import React, { useEffect, useMemo, useState } from 'react';
import { Button, Chip, Link, styled, Typography } from '@mui/material';
import { colors } from '../styles/globalStyles';
import Layout from '../components/Layout';
import StarOrMessageChip from '../components/chips/starOrMessageChip';
import ProgressComponent from '../components/progressComponent';
import ProgressTracker from '../components/progressTracker';
import { ReactComponent as ColoredDot } from '../images/colored_dot.svg';
import { ReactComponent as ArrowRight } from '../images/arrow_right.svg';
import Bg from '../images/bg.png';

import {
    kssDataAtom,
    userForAdminPanelAtom,
    userAtom,
    userKssTrackerDataForAdminPanelAtom,
} from '../recoil/atoms/userAtom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useNavigate } from 'react-router-dom';
import { getMyProgress } from '../api/stats';
import { getAllTimeAvgFeedbackRates } from '../api/score';
import { dateFormatDiff, timeFormatDiff } from '../functions/nextSessionFormat';
import { avgBehaviorsRateAtom, progressDataAtom } from '../recoil/atoms/mainPanelPageAtoms';
import PatternedButton from '../components/inputs/patternedButton';
import StoriesGuide from '../components/storiesGuide';
import StoryModal from '../components/modals/storyModal';
import stories from '../data/stories.json';
import { getWatchedStoriesData } from '../api/stories';
import data from '../data/data.json';
import { behaviorsDataAtom, nextSessionAtom, weekProgressDataAtom } from '../recoil/atoms/mySessionPageAtoms';
import { teamMembersAtom } from '../recoil/atoms/teamMembersAtom';
import { kssTrackerDataAtom, timeLineDataAtom } from '../recoil/atoms/trackerPageAtoms';
import { selectedLanguageAtom } from '../recoil/atoms/languageAtom';
import { ScoresWrapper } from '../components/ui/ScoresWrapper/styles.css';

const FeedbackAndSessions = styled('div')({
    display: 'flex',
    gap: 2,

    letterSpacing: '0.01em',
});

const Feedback = styled('section')({
    padding: 17,
    width: '100%',

    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',

    background: colors.transparentMain,
    borderRadius: 40,
});

const TypographyHeader = styled(Typography)({
    paddingInline: 10,
    fontSize: 24,
    fontWeight: 700,
    lineHeight: '120%',
    color: colors.darkBlue,
});

const BehaviorNameBox = styled('div')({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'start',
    alignItems: 'center',
    gap: 5,
    marginTop: 17,
    marginBottom: 4,
    marginLeft: 7,
});

const TypographyAboveChip = styled(Typography)({
    display: 'flex',
    alignItems: 'center',

    fontSize: 12,
    fontWeight: 500,
    lineHeight: '125%',
    color: colors.main,

    '& svg': {
        marginRight: 5,
    },
    '&.sessions': {
        display: 'block',
        marginBottom: 10,
        paddingInline: 10,
        fontSize: 14,
        lineHeight: '100%',
        color: colors.white,
        opacity: 0.6,
    },
});

const Sessions = styled('section')({
    padding: 17,
    width: '100%',

    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',

    background: `url(${Bg}), radial-gradient(140.07% 100% at 0% 100%, #9C17CB 53.17%, #CB1799 100%)`,
    backgroundSize: 'cover',
    borderRadius: 40,
});

const StyledChip = styled(Chip)({
    marginBottom: 17,
    padding: 17,

    width: '100%',
    height: 87,
    display: 'block',

    background: colors.transparentWhite,
    borderRadius: 23,

    '& span': {
        padding: 0,
    },
});

const Time = styled(Typography)({
    fontSize: 32,
    fontWeight: 700,
    lineHeight: '110%',
    color: colors.white,
});

const DateTypography = styled(Typography)({
    fontSize: 16,
    fontWeight: 400,
    lineHeight: '110%',
    color: colors.white,
});

const StyledButton = styled(Button)({
    width: '100%',
    height: 52,

    background: colors.white,
    backgroundSize: 'cover',
    borderRadius: 23,

    fontSize: 12,
    fontWeight: 700,
    lineHeight: '100%',
    color: '#BD17CB',

    '& span': {
        marginLeft: 17,
    },
    '&:hover': {
        backgroundColor: colors.white,
    },
});

const MainPanelWrap = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    paddingTop: 17,
    gap: 17,
});

const TopBar = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
});

const Greeting = styled(Typography)({
    fontSize: 32,
    lineHeight: '105%',
    letterSpacing: '0.01em',
    color: colors.darkBlue,
    marginLeft: 17,

    '& .accent': {
        color: colors.main,
    },
});

const Title = styled(Typography)({
    fontSize: '30px',
    textAlign: 'center',
});

const Caption = styled(Typography)(({ theme }) => ({
    fontSize: 14,
    opacity: 0.8,
    lineHeight: 1.5,
    padding: '0 10px',
    textAlign: 'center',
}));

const MainPanel = () => {
    const selectedLanguage = useRecoilValue(selectedLanguageAtom);
    const { mainPanel } = data[selectedLanguage];
    const translatedStories = stories[selectedLanguage];
    const [userInfo, setUserInfo] = useRecoilState(userAtom);
    const weekProgressData = useRecoilValue(weekProgressDataAtom);
    const behaviorsData = useRecoilValue(behaviorsDataAtom);
    const nextSession = useRecoilValue(nextSessionAtom);
    const teamMembers = useRecoilValue(teamMembersAtom);
    const kssTrackerData2 = useRecoilValue(kssTrackerDataAtom);
    const timeLineData = useRecoilValue(timeLineDataAtom);
    const language = useRecoilValue(selectedLanguageAtom);

    const [progressData, setProgressData] = useRecoilState(progressDataAtom);
    const [avgBehaviorsRate, setAvgBehaviorsRate] = useRecoilState(avgBehaviorsRateAtom);

    const userKssData = useRecoilValue(kssDataAtom);
    // const userTrackerData = useRecoilValue(kssTrackerDataAtom);
    const userTrackerForAdminPanel = useRecoilValue(userKssTrackerDataForAdminPanelAtom);
    const [userForAdminPanel, setUserForAdminPanel] = useRecoilState(userForAdminPanelAtom);

    const kssTrackerData = localStorage.getItem('user-id-for-admin-panel') ? userTrackerForAdminPanel : userKssData;

    const [hasWatchedStories, setHasWatchedStories] = useState(true);
    const storySetId = 2;

    const userIdForAdminPanel = localStorage.getItem('user-id-for-admin-panel');

    const finalUserData = userIdForAdminPanel ? userForAdminPanel : userInfo;

    const timeHours = useMemo(
        () => timeFormatDiff(finalUserData.session?.next_date, finalUserData.session?.meeting_date),
        [finalUserData.session],
    );
    const timeDate = useMemo(
        () => dateFormatDiff(finalUserData.session?.next_date, finalUserData.session?.meeting_date, selectedLanguage),
        [finalUserData.session],
    );

    let navigate = useNavigate();

    function handleAdminPanelButtonClick() {
        const teamId = localStorage.getItem('team-id');
        const isOnOtherUserAccount = Boolean(localStorage.getItem('user-id-for-admin-panel'));

        localStorage.removeItem('user-id-for-admin-panel');
        setUserForAdminPanel({});

        if (teamId) {
            if (isOnOtherUserAccount) {
                navigate(`/admin/team/${teamId}/users`);
            } else {
                navigate(`/admin/team/${teamId}`);
            }
        } else {
            navigate(`/admin`);
        }
    }

    useEffect(() => {
        if (userIdForAdminPanel) {
            getMyProgress(userIdForAdminPanel)
                .then(({ data }) => {
                    setProgressData(data);
                })
                .catch((err) => {
                    console.error('Ошибка получения прогресса', err);
                });

            getAllTimeAvgFeedbackRates(userIdForAdminPanel)
                .then(({ data }) => {
                    setAvgBehaviorsRate(data);
                })
                .catch((err) => {
                    console.error('Ошибка получения средних оценок', err);
                });
        } else {
            getMyProgress()
                .then(({ data }) => {
                    setProgressData(data);
                })
                .catch((err) => {
                    console.error('Ошибка получения прогресса', err);
                });

            getAllTimeAvgFeedbackRates(userInfo.userId)
                .then(({ data }) => {
                    setAvgBehaviorsRate(data);
                })
                .catch((err) => {
                    console.error('Ошибка получения средних оценок', err);
                });
        }

        if (!window.Telegram?.WebApp?.initDataUnsafe?.user?.id) {
            return;
        }

        getWatchedStoriesData(window.Telegram?.WebApp?.initDataUnsafe?.user?.id)
            .then((res) => {
                const watchedSets = res.data?.watched_story_sets;
                const isWatched = watchedSets.includes(storySetId);

                if (isWatched) {
                    return;
                }

                setHasWatchedStories(false);
            })
            .catch((err) => {
                console.error(err);
            });
    }, []);

    useEffect(() => {
        console.log('userInfo', userInfo);
    }, [userInfo]);
    useEffect(() => {
        console.log('userForAdminPanel', userForAdminPanel);
    }, [userForAdminPanel]);
    useEffect(() => {
        console.log('progressData', progressData);
    }, [progressData]);
    useEffect(() => {
        console.log('avgBehaviorsRate', avgBehaviorsRate);
    }, [avgBehaviorsRate]);
    useEffect(() => {
        console.log('weekProgressData', weekProgressData);
    }, [weekProgressData]);
    useEffect(() => {
        console.log('behaviorsData', behaviorsData);
    }, [behaviorsData]);
    useEffect(() => {
        console.log('nextSession', nextSession);
    }, [nextSession]);
    useEffect(() => {
        console.log('teamMembers', teamMembers);
    }, [teamMembers]);
    useEffect(() => {
        console.log('userKssData', userKssData);
    }, [userKssData]);
    useEffect(() => {
        console.log('kssTrackerData2', kssTrackerData2);
    }, [kssTrackerData2]);
    useEffect(() => {
        console.log('timeLineData', timeLineData);
    }, [timeLineData]);
    useEffect(() => {
        console.log('language', language);
    }, [language]);

    return (
        <Layout isLangDropdown={true}>
            {!hasWatchedStories && userInfo.isSessionCreated && (
                <StoryModal
                    storiesGroup={translatedStories.find((group) => group.id === storySetId)}
                    isAutoOpen={true}
                />
            )}
            <MainPanelWrap>
                <TopBar>
                    <Chip label={mainPanel.page} variant={'lightBlue'} sx={{ height: 20 }} />
                    <Link sx={{ color: colors.main, cursor: 'pointer' }} onClick={() => navigate('/support')}>
                        {mainPanel.info}
                    </Link>
                </TopBar>
                {userInfo.isLeader ? (
                    <PatternedButton label={mainPanel.adminButton} onClick={handleAdminPanelButtonClick} />
                ) : null}
                {window.Telegram?.WebApp?.initDataUnsafe?.user && !userInfo.isSessionCreated ? (
                    userInfo.currentUsersNumber < userInfo.numbersOfUsers ? (
                        <>
                            <Title>{mainPanel.userInfo.firstBlock.title}</Title>
                            <Caption>
                                {mainPanel.userInfo.firstBlock.text.main} {userInfo.currentUsersNumber || '0'}{' '}
                                {mainPanel.userInfo.firstBlock.text.preposition} {userInfo.numbersOfUsers || '0'}{' '}
                                {mainPanel.userInfo.firstBlock.text.secondary}
                            </Caption>
                            <Caption dangerouslySetInnerHTML={{ __html: mainPanel.userInfo.firstBlock.description }} />
                        </>
                    ) : (
                        <>
                            <Title>{mainPanel.userInfo.secondBlock.title}</Title>
                            <Caption>{mainPanel.userInfo.secondBlock.secondaryTitle}</Caption>

                            {userInfo.areValuesApproved ? (
                                <Caption>{mainPanel.userInfo.secondBlock.text.main}</Caption>
                            ) : (
                                <Caption
                                    dangerouslySetInnerHTML={{ __html: mainPanel.userInfo.secondBlock.description }}
                                />
                            )}
                        </>
                    )
                ) : (
                    <>
                        <Greeting variant={'h2'}>
                            <span className={'accent'}>{mainPanel.greetings} </span> {finalUserData.firstName}
                        </Greeting>
                        {window.Telegram?.WebApp?.initDataUnsafe?.user && <StoriesGuide />}
                        <ProgressComponent percentage={progressData.progress} period={progressData.period} />
                        <ProgressTracker mainPanel hasKssData={kssTrackerData.length > 0} />
                        <FeedbackAndSessions>
                            <Feedback>
                                <TypographyHeader>{mainPanel.behavior.feedback}</TypographyHeader>
                                <div style={{ width: '100%' }}>
                                    <BehaviorNameBox>
                                        <ColoredDot style={{ minWidth: 10, minHeight: 10 }} fill={colors.green} />
                                        <TypographyAboveChip>{mainPanel.behavior.positive}</TypographyAboveChip>
                                    </BehaviorNameBox>
                                    <div style={{ height: '64px', width: '100%' }}>
                                        <ScoresWrapper columns={1}>
                                            <StarOrMessageChip
                                                number={avgBehaviorsRate?.positive ?? '-'}
                                                radius={23}
                                                height={64}
                                                background={colors.transparentMain}
                                                stars={avgBehaviorsRate?.positive ?? -1}
                                            />
                                        </ScoresWrapper>
                                    </div>
                                </div>
                                <div style={{ width: '100%' }}>
                                    <BehaviorNameBox>
                                        <ColoredDot style={{ minWidth: 10, minHeight: 10 }} fill={colors.red} />
                                        <TypographyAboveChip>{mainPanel.behavior.negative}</TypographyAboveChip>
                                    </BehaviorNameBox>
                                    <div style={{ height: '64px', width: '100%' }}>
                                        <ScoresWrapper columns={1}>
                                            <StarOrMessageChip
                                                number={avgBehaviorsRate?.negative ?? '-'}
                                                radius={23}
                                                height={64}
                                                background={colors.transparentMain}
                                                stars={avgBehaviorsRate?.negative ?? -1}
                                            />
                                        </ScoresWrapper>
                                    </div>
                                </div>
                            </Feedback>
                            <Sessions>
                                <TypographyHeader sx={{ color: colors.white }}>
                                    {timeHours ? mainPanel.sessions.title : mainPanel.sessions.secondaryTitle}
                                </TypographyHeader>
                                {timeHours && (
                                    <>
                                        <div>
                                            <TypographyAboveChip className="sessions">
                                                {mainPanel.sessions.text}
                                            </TypographyAboveChip>
                                            <StyledChip
                                                label={
                                                    <>
                                                        <Time>{timeHours}</Time>
                                                        <DateTypography>{timeDate}</DateTypography>
                                                    </>
                                                }
                                            />
                                        </div>
                                        <StyledButton
                                            onClick={() => navigate('/my-sessions')}
                                            endIcon={<ArrowRight fill="#BD17CB" />}
                                        >
                                            {mainPanel.sessions.button}
                                        </StyledButton>
                                    </>
                                )}
                            </Sessions>
                        </FeedbackAndSessions>
                        <PatternedButton label={mainPanel.instructionsButton} onClick={() => navigate('/support')} />
                    </>
                )}
            </MainPanelWrap>
        </Layout>
    );
};

export default MainPanel;
